import React from 'react';
import { App as AntApp } from 'antd';
import { BrowserRouter, Routes, Route } from 'react-router-dom';

import { ToastHost } from '@spark';

import SideBar from '@/app/layout/SideBar';
import { GlobalProvider } from '@/app/providers/globalState';
import { AuthProvider, RequireSaas, NO_ACCESS_PATH } from '@/features/auth/AuthContext';
import AuthBoundary from '@/features/auth/components/AuthBoundary';
import NoAccessPage from '@/features/auth/components/NoAccessPage';
import { WaitlistLanding, WaitlistApplyPage } from '@/features/waiting-list';
import CreateWorkspacePage from '@/features/workspaces/components/CreateWorkspacePage';
import Login from '@/pages/Login.jsx';

/**
 * The one route table shared by both browser entries (`src/app/main.jsx`
 * for `index.html`, `src/index.jsx` for `public/index-fullstory.html`).
 * Production serves the FullStory entry, so a route that only existed in
 * one table used to work in development and 404 (or spin forever) live.
 *
 * Everything sits behind `AuthBoundary`: no page mounts until the session
 * bootstrap has settled and any bootstrap-driven redirect has been applied.
 */
export function AppRoutes() {
    return (
        <AuthBoundary>
            <Routes>
                {/* Enterprise SSO only: the self-service signup/verify/forgot flows were removed. */}
                <Route path="/login" element={<Login />} />
                {/* Waiting list (SaaS only): public landing + the authenticated questionnaire, both outside the SideBar shell. */}
                <Route
                    path="/waitlist"
                    element={(
                        <RequireSaas>
                            <WaitlistLanding />
                        </RequireSaas>
                    )}
                />
                <Route
                    path="/waitlist/apply"
                    element={(
                        <RequireSaas>
                            <WaitlistApplyPage />
                        </RequireSaas>
                    )}
                />
                {/* Self-hosted counterpart: strangers have no waiting list and can only be invited by a workspace admin. */}
                <Route path={NO_ACCESS_PATH} element={<NoAccessPage />} />
                {/* Onboarding wizard for an approved Creator without a workspace; same full-page shell as the waiting list. */}
                <Route path="/create-workspace" element={<CreateWorkspacePage />} />
                <Route
                    path="/*"
                    element={(
                        <GlobalProvider>
                            <SideBar />
                        </GlobalProvider>
                    )}
                />
            </Routes>
        </AuthBoundary>
    );
}

export default function App() {
    return (
        <div className="app-wrapper" style={{ height: '100%', overflow: 'hidden' }}>
            <BrowserRouter>
                <AntApp>
                    <AuthProvider>
                        <AppRoutes />
                    </AuthProvider>
                </AntApp>
            </BrowserRouter>
            <ToastHost />
        </div>
    );
}
